import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Event, EventInput, Task } from '@underline/shared';
import { api } from '@/lib/api';

export interface EventWithTasks extends Event {
  tasks: Task[];
}

interface ListParams {
  from?: string;
  to?: string;
  brand_id?: number;
}

export function useEvents(params: ListParams = {}) {
  return useQuery({
    queryKey: ['events', params],
    queryFn: () =>
      api
        .get('events', {
          searchParams: Object.fromEntries(
            Object.entries(params).filter(([, v]) => v != null),
          ) as Record<string, string | number>,
        })
        .json<EventWithTasks[]>(),
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EventInput) => api.post('events', { json: input }).json<EventWithTasks>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<EventInput> }) =>
      api.patch(`events/${id}`, { json: input }).json<Event>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`events/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}
